import React from 'react';
import { noop, map, fromPairs, isEqual, get, size, find, isNil, isEmpty  } from 'lodash';
import classnames from 'classnames';
import { button as Button } from 'modules/button';

// local dependencies
import { Form } from 'modules/form';
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { tableSearchInlineWidgetPreloader as TableSearchInlineWidgetPreloader } from 'ui/views/widgets/tableSearchInlineWidget';
import { popover } from 'modules/popover';
import { DATA_TYPES } from 'constants/common';
import { widgetError as WidgetError } from 'ui/views/widgets/widgetError';
import { WIDGET_ERROR_TYPE } from 'constants/common';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { cSrMemberNotes as CSrMemberNotes } from 'ui/views/widgets/cSRMemberNotes';
 *
 * // render
 * <CSrMemberNotes propName='propValue'/>
*/

const ViewNotes = ( props ) => {

    const epostNotes = get( props.data, 'data.state.epostNotes' );
    const noteList = get( props.data, 'structure.noteList' );

    return(
        <div className='ui-csr-member-notes__body'>
            <div className='ui-csr-member-notes__body__list__items'>
                { 0 === size( epostNotes ) && (
                    <WidgetError type={  WIDGET_ERROR_TYPE.NO_DATA_FOUND } />
                ) }

                { map( epostNotes, note => {
                    return (
                        <div className='ui-csr-member-notes__body__list__items__item' key={ note.date } >
                            <div className='ui-csr-member-notes__body__list__items__item__top' >
                                <div className='ui-csr-member-notes__body__list__items__item__top__reason' >
                                    { note.reason }
                                </div>
                                <div className='ui-csr-member-notes__body__list__items__item__top__date' >
                                    { getLabel( find( noteList, { name: 'date' } ).title ) } { note.date }
                                </div>
                            </div>
                            <div className='ui-csr-member-notes__body__list__items__item__center' >

                                { ( !isNil( get( note, 'order', undefined ) ) && !isEmpty( get( note, 'order', '' ) ) ) && (
                                    <div className='ui-csr-member-notes__body__list__items__item__center__order' >
                                        <div className='ui-csr-member-notes__body__list__items__item__center__order__label' >
                                            { getLabel( find( noteList, { name: 'orderInvoiceNumber' } ).title ) + ': '}
                                        </div>
                                        <div className='ui-csr-member-notes__body__list__items__item__center__order__value' >
                                            { note.order }
                                        </div>
                                    </div>
                                ) }
                                
                                <div className='ui-csr-member-notes__body__list__items__item__center__originator' >
                                    <div className='ui-csr-member-notes__body__list__items__item__center__originator__label' >
                                        { getLabel( find( noteList, { name: 'originatorType' } ).title ) }
                                    </div>
                                    <div className='ui-csr-member-notes__body__list__items__item__center__originator__value' >
                                        { note.originatorType.name } { '(' + note.originatorType.position + ')' }
                                    </div>
                                </div>
                            </div>

                            { ( !isNil( get( note, 'rxNumber', undefined ) ) && !isEmpty( get( note, 'rxNumber', '' ) ) ) && (
                                <div className='ui-csr-member-notes__body__list__items__item__bottom' >
                                    <div className='ui-csr-member-notes__body__list__items__item__bottom__rx' >
                                        <div className='ui-csr-member-notes__body__list__items__item__bottom__rx__label' >
                                            { getLabel( find( noteList, { name: 'rxNumber' } ).title ) + ': '}
                                        </div>
                                        <div className='ui-csr-member-notes__body__list__items__item__bottom__rx__value' >
                                            { note.rxNumber }
                                        </div>
                                    </div>
                                </div>
                            ) }

                            <div className='ui-csr-member-notes__body__list__items__item__comment' >
                                <div className='ui-csr-member-notes__body__list__items__item__comment__value' >
                                    { note.comment }
                                </div>
                            </div>
                            
                        </div>
                    );
                } ) }

            </div>

                    {/* button actions */}
            <div className='ui-csr-member-notes__body__actions'>
                { map( props.data.actions, action => {
                    return (
                        <Button
                            key={ action.name }
                            { ...action }
                            outline={ 'cancel' === action.name ? true: false }
                            size='large'
                            className='ui-csr-member-notes__body__actions__action'
                            onClick={ () => 'cancel' !== action.name ? props.setAdd( true ) : props.onCancelClick() }
                        />
                    );
                } ) }
        </div>
    </div>
    );
};

const AddNotes = ( props ) => {

    const formActions = get( props.data, 'structure.formActions', [] );
    const formFields = get( props.data, 'structure.fields', '' );
    const options = get( props.data, 'data.options', '' );
            
    return (
        <div className='ui-csr-member-notes__form-section'>
            <Form
                fields={ fromPairs( map( formFields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
                isValid={false}
                mode={props.mode}
                onSubmit={( { values, isValid } ) => {
                    if ( isValid ) {
                        props.onSubmitClick( {
                            ...values
                        } );
                    }
                }}
            >
                {( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {

                    return (
                        <form
                            onSubmit={onSumbit}
                            className='ui-csr-member-notes__form-section__form'
                        >
                            { /* fields */}
                            { map( fields, ( field ) => {

                                const fieldClassName = classnames( 'ui-csr-member-notes__form-section__form__field',
                                    `ui-csr-member-notes__form-section__form__field--${field.name}`
                                );

                                return (
                                    <field.view
                                        className= { fieldClassName }
                                        key={ field.name }
                                        name={ field.name }
                                        required={ field.required }
                                        placeholder={ field.placeholder }
                                        label={ field.title }
                                        onChange={ onChange }
                                        value={ values[ field.name ] }
                                        error={ errors[ field.name ] }
                                        url={ get( field, 'url', {} ) }
                                        options={ get( options, field.options, [] ) }
                                        hideIcon = { field.type !== DATA_TYPES.SEARCH }
                                        maxLength={ field.maxLength }
                                        searchable={ get( field, 'config.searchable', false ) }
                                        searchType={ get( field, 'config.searchType', DATA_TYPES.SINGLE_SELECT ) }
                                        requestPayload={ props.requestPayload }
                                    />
                                );
                            } )}

                            {/* button actions */}
                            <div className='ui-csr-member-notes__form-section__form__actions'>
                                {map( formActions, action => {
                                    return (
                                        <Button
                                            key={action.name}
                                            id={action.name}
                                            type={ 'cancel' !== action.name ? 'submit' : 'button' }
                                            disabled={'cancel' !== action.name ? !isValid : false }
                                            {...action}
                                            outline={ 'cancel' === action.name ? true : false }
                                            size='large'
                                            className='ui-csr-member-notes__form-section__form__actions__action'
                                            onClick={ isEqual( 'cancel', action.name ) ? () => {
                                                popover.close();
                                                props.setAdd( false );
                                            } : null }
                                        />
                                    );
                                } ) }
                            </div>
                        </form>
                    );
                }}
            </Form>
        </div>
    );
};

export const cSrMemberNotesSuccess = ( props ) => {

    // const [ isAdd, setAdd ] = useState( false );

    const className = classnames( 'ui-csr-member-notes', {
        'ui-csr-member-notes--form-view': props.isAdd
    } );

    return (
        <div className={ className }>
            <div className='ui-csr-member-notes__header'>

                {/* title for the modal */}
                <div className='ui-csr-member-notes__header__title'>
                    { getLabel( props.data.title ) }
                </div>

                {/* close button */}
                <div className='ui-error-overlay__head__close' onClick={ () => props.closeOverlay() }>
                    <Icon name='close' width='14' height='14' fill='#ffffff' />
                </div>
            </div>

        { ! props.isAdd ?
            <ViewNotes { ...props } setAdd={ ( val ) => props.setAdd( val ) } />
        :
            <AddNotes { ...props } setAdd={ ( val ) => props.setAdd( val ) } />
        }
        </div>
    );
};

// set display name
cSrMemberNotesSuccess.displayName = 'cSrMemberNotesSuccess';


// set default props
cSrMemberNotesSuccess.defaultProps = {
    componentData: {},
    layoutConfig: {},
    onActionClick: noop,
 };
 
 // prop types
 cSrMemberNotesSuccess.propTypes = {};
 
 /*******************************/
 
 // preloader view
 export const cSrMemberNotesPreloader = ( props ) => {
 
     // widget class name
     const widgetClassName = classnames( 'ui-call-list-widget-preloader', props.className );
 
     return (
         <div className={ widgetClassName }>
             <TableSearchInlineWidgetPreloader />
         </div>
     );
 };
 
 // set display name
 cSrMemberNotesPreloader.displayName = 'cSrMemberNotesPreloader';
 
 // set default props
 cSrMemberNotesPreloader.defaultProps = {};
 
 // prop types
 cSrMemberNotesPreloader.propTypes = {};
 
 /*******************************/
 
 /**
  * @type {object}
  * @desc A React presentation/dumb view component.
  * @example
  * import { cSrMemberNotes as cSrMemberNotes } from 'ui/views/widgets/cSrMemberNotes';
  *
  * // render
  * <cSrMemberNotes propName='propValue'/>
  */
 export const cSrMemberNotes = withNetworkState( cSrMemberNotesPreloader, cSrMemberNotesSuccess );
